// src/pages/admin/AdminFunnelCampaignsPage.tsx
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FullScreenLoader } from "components/ui/Loader";
import { getErrorMessage } from "utils/errors";
import {
  adminAddFunnelCampaign,
  adminListFunnelCampaigns,
  adminRemoveFunnelCampaign,
  type FunnelCampaign,
  type FunnelCampaignsOut,
} from "api/adminFunnelCampaigns";

/**
 * The campaign names the app funnel may send to Plausible (DG-0057). Only a name on
 * this list is sent. The server holds the name rule and refuses anything else, so this
 * page shows the rule the server returns and the server's own 422 message, and never
 * checks or tidies a name itself.
 */

function formatWhen(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

/** The server's per-field messages from a 422, keyed by field. Empty for any other error. */
function fieldErrors(e: unknown): Record<string, string> {
  const err = e as { status?: number; detail?: unknown };
  if (err?.status !== 422 || !Array.isArray(err.detail)) return {};
  const out: Record<string, string> = {};
  for (const d of err.detail as Array<{ field?: unknown; message?: unknown }>) {
    if (typeof d?.field === "string" && typeof d?.message === "string" && !out[d.field]) {
      out[d.field] = d.message;
    }
  }
  return out;
}

function statusOf(row: FunnelCampaign): string {
  if (row.source === "config") return "Set in config";
  return row.removed_at ? "Removed" : "Live";
}

const buttonClass = [
  "inline-flex items-center justify-center rounded-full",
  "border border-slate-200 bg-white",
  "px-4 py-2",
  "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
  "hover:bg-slate-50 transition-colors font-ui font-bold",
  "disabled:opacity-50",
].join(" ");

const AdminFunnelCampaignsPage: React.FC = () => {
  const [data, setData] = useState<FunnelCampaignsOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [includeRemoved, setIncludeRemoved] = useState(false);

  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);

  // Only the newest request may update the page, so a slow older answer cannot land last.
  const latest = useRef(0);
  // The toggle as it is now, so a reload after an add or remove that was started before
  // the toggle changed asks for what the checkbox shows, not what it showed then.
  const includeRemovedNow = useRef(includeRemoved);
  useLayoutEffect(() => {
    includeRemovedNow.current = includeRemoved;
  }, [includeRemoved]);

  const load = useCallback(async () => {
    const request = ++latest.current;
    setErr("");
    try {
      const res = await adminListFunnelCampaigns({ includeRemoved: includeRemovedNow.current });
      if (request !== latest.current) return;
      setData(res);
    } catch (e: unknown) {
      if (request !== latest.current) return;
      // Never leave an older list up beside the error: it would read as what is sent now.
      setData(null);
      setErr(getErrorMessage(e, "Failed to load campaign names"));
    } finally {
      if (request === latest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, includeRemoved]);

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddErr("");
    setAdding(true);
    try {
      await adminAddFunnelCampaign(name);
      setName("");
      await load();
    } catch (e: unknown) {
      const fields = fieldErrors(e);
      setAddErr(fields.name ?? Object.values(fields)[0] ?? getErrorMessage(e, "Could not add the name"));
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (campaign: string) => {
    setErr("");
    setRemoving(campaign);
    try {
      await adminRemoveFunnelCampaign(campaign);
      await load();
    } catch (e: unknown) {
      setErr(getErrorMessage(e, "Could not remove the name"));
    } finally {
      setRemoving(null);
    }
  };

  if (loading) return <FullScreenLoader label="Loading…" />;

  const items = data?.items ?? [];

  return (
    <main className="relative min-h-screen px-4 py-12 sm:py-16">
      <header className="text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]">
          Admin · Funnel campaign names
        </h1>
      </header>

      <section className="mt-16 sm:mt-20 flex justify-center">
        <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8">
          <Link
            to="/admin"
            className="font-ui text-xs uppercase tracking-wide text-slate-500 hover:opacity-80"
          >
            ← Back to Admin Home
          </Link>

          <p className="mt-6 font-body text-slate-600">
            The app funnel sends a campaign name to Plausible only if it is on this list.
            Removing a name stops it being sent from the next send.
          </p>

          <form onSubmit={onAdd} className="mt-6" noValidate>
            <label htmlFor="funnel-campaign-name" className="block font-ui text-sm text-slate-700">
              Add a campaign name
            </label>
            <div className="mt-2 flex flex-wrap items-start gap-3">
              <input
                id="funnel-campaign-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  // An error is about the name that was sent, so it goes once the name changes.
                  setAddErr("");
                }}
                // Locked while saving, so clearing it on success cannot wipe a second name.
                readOnly={adding}
                aria-invalid={addErr ? true : undefined}
                aria-describedby="funnel-campaign-rule"
                className="w-full sm:w-80 rounded border border-slate-300 px-3 py-2 font-body text-slate-900"
              />
              <button type="submit" disabled={adding || !name} className={buttonClass}>
                Add
              </button>
            </div>
            {data?.rule && (
              <p id="funnel-campaign-rule" className="mt-2 font-body text-sm text-slate-500">
                Allowed: {data.rule}
              </p>
            )}
            {addErr && (
              <p role="alert" className="mt-2 font-body text-sm text-red-700">
                {addErr}
              </p>
            )}
          </form>

          <label className="mt-8 flex items-center gap-2 font-body text-sm text-slate-700">
            <input
              type="checkbox"
              checked={includeRemoved}
              onChange={(e) => setIncludeRemoved(e.target.checked)}
            />
            Show removed names
          </label>

          {err && (
            <div className="mt-6 border border-red-300 bg-red-50 text-red-700 p-3 rounded">
              {err}
            </div>
          )}

          <div className="mt-6">
            {!err && items.length === 0 ? (
              <div className="font-body text-slate-600">No campaign names yet.</div>
            ) : items.length > 0 ? (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-700 font-ui text-xs uppercase tracking-wide">
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Added</th>
                      <th className="text-left p-3">Removed</th>
                      <th className="text-left p-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr
                        key={`${row.name}|${row.source}|${row.added_at ?? ""}`}
                        className="border-t border-slate-200"
                      >
                        <td className="p-3 font-body text-slate-900 break-words">{row.name}</td>
                        <td className="p-3 font-body text-slate-700">{statusOf(row)}</td>
                        <td className="p-3 font-body text-slate-700">
                          {row.added_by ? `${row.added_by}, ${formatWhen(row.added_at)}` : "-"}
                        </td>
                        <td className="p-3 font-body text-slate-700">
                          {row.removed_at ? `${row.removed_by ?? "-"}, ${formatWhen(row.removed_at)}` : "-"}
                        </td>
                        <td className="p-3">
                          {row.source === "admin" && !row.removed_at && (
                            <button
                              type="button"
                              onClick={() => void onRemove(row.name)}
                              disabled={removing !== null}
                              aria-label={`Remove ${row.name}`}
                              className={buttonClass}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
};

export default AdminFunnelCampaignsPage;
